import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Select, Typography, Space, Divider, Button, Row, Col, Spin, message, Input, Checkbox, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import IntlMessages from '@iso/components/utility/intlMessages';
import { incidentService, propertyService } from '@iso/services';
import { TYPES, NOTIFICATION_TRIGGERS, NOTIFICATION_CHANNELS } from '@iso/constants/incidentOptions';
import styled from 'styled-components';

const { Paragraph, Title, Text } = Typography;
const { Option } = Select;
const BRAND_COLOR = '#0B2A1B';
const BRAND_SHADOW = '0 0 0 2px rgba(11, 42, 27, 0.25)';

const StyledCancelButton = styled(Button)`
  border-color: ${BRAND_COLOR};
  color: ${BRAND_COLOR};
  background: #fff;
  &:hover,
  &:focus {
    color: ${BRAND_COLOR};
    border-color: ${BRAND_COLOR};
    box-shadow: ${BRAND_SHADOW};
    background: #f4f8f5;
  }
`;

const StyledSaveButton = styled(Button)`
  background: ${BRAND_COLOR};
  border-color: ${BRAND_COLOR};
  color: #fff;
  &:hover,
  &:focus {
    background: ${BRAND_COLOR};
    border-color: ${BRAND_COLOR};
    color: #fff;
    box-shadow: ${BRAND_SHADOW};
  }
  &:disabled {
    background: #9fb4a9;
    border-color: #9fb4a9;
    box-shadow: none;
  }
`;

const RuleCard = styled.div`
  border: 1px solid #e6ebe8;
  border-radius: 6px;
  padding: 16px 16px 0;
  background: #fbfdfc;

  .ant-form-item {
    margin-bottom: 16px;
  }
`;

const DEFAULT_SETTINGS = {
  incident_manager_ids: [],
  notification_rules: [],
};

const DEFAULT_CHANNELS = ['email'];

const EMPTY_RULE = {
  code: undefined,
  label: '',
  trigger_event: 'manual',
  incident_type: null,
  role_types: [],
  user_ids: [],
  channels: DEFAULT_CHANNELS,
};

/**
 * Build a stable rule code from its label. Dispatch history is keyed by code,
 * so a new rule needs one that does not shift when rows are reordered.
 *
 * @param {string} label
 * @returns {string}
 */
const buildRuleCode = (label) =>
  String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);

const buildUserLabel = (user) => {
  if (!user) {
    return '';
  }
  if (user.name && user.email && user.name !== user.email) {
    return `${user.name} (${user.email})`;
  }
  return user.name || user.email || '';
};

const toNumberList = (values) =>
  Array.isArray(values)
    ? values.map((value) => Number(value)).filter((value) => !Number.isNaN(value))
    : [];

const normalizeManagerIds = (settings) => {
  const safeSettings = settings && typeof settings === 'object' ? settings : {};
  return toNumberList(safeSettings.incident_manager_ids);
};

/**
 * Shape a rule from the rules endpoint into a form row. Built-in rules arrive
 * with no id and are editable like any other — saving one persists it as an
 * override for this property.
 *
 * @param {object} rule
 * @returns {object}
 */
const normalizeRule = (rule) => ({
  code: rule?.code || undefined,
  label: rule?.label || '',
  trigger_event: rule?.trigger_event || 'manual',
  incident_type: rule?.incident_type || null,
  role_types: Array.isArray(rule?.role_types) ? rule.role_types : [],
  user_ids: toNumberList(rule?.user_ids),
  channels: Array.isArray(rule?.channels) && rule.channels.length > 0 ? rule.channels : DEFAULT_CHANNELS,
});

const IncidentSettingsModal = ({ visible, onCancel, propertyId, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  const loadUsers = useCallback(
    async (property) => {
      if (!property) {
        setUserOptions([]);
        return;
      }
      try {
        const response = await propertyService.getListUsersOfProperty(property, true);
        const users = Array.isArray(response?.users) ? response.users : [];
        setUserOptions(users);
      } catch (error) {
        console.error('Failed to load property users', error);
        setUserOptions([]);
      }
    },
    []
  );

  const loadSettings = useCallback(
    async (property) => {
      if (!property) {
        form.setFieldsValue(DEFAULT_SETTINGS);
        return;
      }
      try {
        // Managers come from the settings endpoint; rules come from the rules
        // endpoint, which merges the property's saved rules over the built-in
        // defaults so a customer sees Notify MD even before customising it.
        const [settingsResponse, rulesResponse] = await Promise.all([
          incidentService.getSettings(property),
          incidentService.getNotificationRules(property),
        ]);

        const rules = Array.isArray(rulesResponse?.rules) ? rulesResponse.rules.map(normalizeRule) : [];
        setRoleOptions(Array.isArray(rulesResponse?.role_types) ? rulesResponse.role_types : []);

        form.setFieldsValue({
          incident_manager_ids: normalizeManagerIds(settingsResponse?.settings || settingsResponse),
          notification_rules: rules.length > 0 ? rules : [{ ...EMPTY_RULE }],
        });
      } catch (error) {
        console.error('Failed to load incident settings', error);
        form.setFieldsValue({
          incident_manager_ids: [],
          notification_rules: [{ ...EMPTY_RULE }],
        });
      }
    },
    [form]
  );

  const initializeModal = useCallback(async () => {
    if (!visible || !propertyId) {
      return;
    }
    setLoading(true);
    try {
      await Promise.all([loadUsers(propertyId), loadSettings(propertyId)]);
    } finally {
      setLoading(false);
    }
  }, [visible, propertyId, loadUsers, loadSettings]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      return;
    }
    initializeModal();
  }, [visible, initializeModal, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        incident_manager_ids: values.incident_manager_ids || [],
        notification_rules: Array.isArray(values.notification_rules)
          ? values.notification_rules
              // A rule that reaches nobody is dropped rather than saved dormant.
              .filter(
                (rule) =>
                  rule &&
                  ((Array.isArray(rule.role_types) && rule.role_types.length > 0) ||
                    (Array.isArray(rule.user_ids) && rule.user_ids.length > 0))
              )
              .map((rule) => ({
                code: rule.code || buildRuleCode(rule.label),
                label: rule.label,
                trigger_event: rule.trigger_event,
                incident_type: rule.incident_type || null,
                role_types: rule.role_types || [],
                user_ids: rule.user_ids || [],
                channels: rule.channels && rule.channels.length > 0 ? rule.channels : DEFAULT_CHANNELS,
                is_active: true,
              }))
          : [],
      };
      const response = await incidentService.saveSettings(propertyId, payload);
      if (response?.code === '0000') {
        message.success(<IntlMessages id="incidentPage.settings.success" />);
        if (typeof onSaved === 'function') {
          onSaved(response.settings || payload);
        }
      } else {
        throw new Error(response?.message || 'Failed to update settings.');
      }
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Failed to update settings.');
    } finally {
      setSaving(false);
    }
  };

  const renderUserSelect = (placeholderId) => (
    <Select
      mode="multiple"
      placeholder={<IntlMessages id={placeholderId} />}
      optionFilterProp="children"
      showSearch
      size="large"
      allowClear
    >
      {userOptions.map((user) => (
        <Option key={user.id} value={user.id}>
          {buildUserLabel(user)}
        </Option>
      ))}
    </Select>
  );

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title={<IntlMessages id="incidentPage.settings.title" />}
      destroyOnClose
      width={720}
      maskClosable={false}
      footer={[
        <StyledCancelButton key="cancel" onClick={onCancel}>
          <IntlMessages id="incidentPage.settings.cancel" />
        </StyledCancelButton>,
        <StyledSaveButton
          key="save"
          type="primary"
          loading={saving}
          onClick={handleSave}
          disabled={loading || !propertyId}
        >
          <IntlMessages id="incidentPage.settings.save" />
        </StyledSaveButton>,
      ]}
    >
      <Spin spinning={loading}>
        <Form layout="vertical" form={form} initialValues={DEFAULT_SETTINGS}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Title level={5}>
              <IntlMessages id="incidentPage.settings.managersLabel" />
            </Title>
            <Paragraph type="secondary">
              <IntlMessages id="incidentPage.settings.managersDescription" />
            </Paragraph>
            <Form.Item name="incident_manager_ids" rules={[{ type: 'array' }]}>
              {renderUserSelect('incidentPage.settings.managerPlaceholder')}
            </Form.Item>
          </Space>

          <Divider />

          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Title level={5}>
              <IntlMessages id="incidentPage.settings.notificationTitle" />
            </Title>
            <Paragraph type="secondary">
              <IntlMessages id="incidentPage.settings.notificationDescription" />
            </Paragraph>
            <Form.List name="notification_rules">
              {(fields, { add, remove }) => (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {fields.map((field) => (
                    <RuleCard key={field.key}>
                      <Row gutter={12}>
                        <Col xs={24} md={10}>
                          <Form.Item
                            name={[field.name, 'label']}
                            fieldKey={[field.fieldKey, 'label']}
                            label={<IntlMessages id="incidentPage.settings.ruleLabel" />}
                            rules={[
                              {
                                required: true,
                                whitespace: true,
                                message: 'Give the notification a name',
                              },
                            ]}
                          >
                            <Input placeholder="Notify MD" size="large" maxLength={100} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={14}>
                          <Form.Item
                            name={[field.name, 'trigger_event']}
                            fieldKey={[field.fieldKey, 'trigger_event']}
                            label={<IntlMessages id="incidentPage.settings.ruleTrigger" />}
                            rules={[{ required: true, message: 'Choose when to send it' }]}
                          >
                            <Select size="large">
                              {NOTIFICATION_TRIGGERS.map((trigger) => (
                                <Option key={trigger.value} value={trigger.value}>
                                  <Tooltip title={trigger.description}>{trigger.label}</Tooltip>
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            noStyle
                            shouldUpdate={(prev, next) =>
                              prev.notification_rules?.[field.name]?.trigger_event !==
                              next.notification_rules?.[field.name]?.trigger_event
                            }
                          >
                            {({ getFieldValue }) => {
                              // An approval notification is matched to an
                              // incident by type, so it needs one; every other
                              // trigger treats "all types" as valid.
                              const isApproval =
                                getFieldValue(['notification_rules', field.name, 'trigger_event']) ===
                                'on_approval';

                              return (
                                <Form.Item
                                  name={[field.name, 'incident_type']}
                                  fieldKey={[field.fieldKey, 'incident_type']}
                                  label={<IntlMessages id="incidentPage.settings.ruleIncidentType" />}
                                  rules={[
                                    {
                                      required: isApproval,
                                      message: 'Approval notifications need an incident type',
                                    },
                                  ]}
                                >
                                  <Select
                                    placeholder={isApproval ? 'Select type' : 'All incident types'}
                                    size="large"
                                    allowClear={!isApproval}
                                    showSearch
                                    optionFilterProp="children"
                                  >
                                    {TYPES.map((type) => (
                                      <Option key={type.value} value={type.value}>
                                        {type.label}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              );
                            }}
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item
                            name={[field.name, 'channels']}
                            fieldKey={[field.fieldKey, 'channels']}
                            label={<IntlMessages id="incidentPage.settings.ruleChannels" />}
                            rules={[
                              { type: 'array', required: true, message: 'Pick at least one channel' },
                            ]}
                          >
                            <Checkbox.Group options={NOTIFICATION_CHANNELS} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={12}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            name={[field.name, 'role_types']}
                            fieldKey={[field.fieldKey, 'role_types']}
                            label={<IntlMessages id="incidentPage.settings.ruleRoles" />}
                            extra={<IntlMessages id="incidentPage.settings.ruleRolesHelper" />}
                          >
                            <Select
                              mode="multiple"
                              placeholder="Select roles"
                              size="large"
                              optionFilterProp="children"
                              showSearch
                              allowClear
                            >
                              {roleOptions.map((role) => (
                                <Option key={role.value} value={role.value}>
                                  {role.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item
                            name={[field.name, 'user_ids']}
                            fieldKey={[field.fieldKey, 'user_ids']}
                            label={<IntlMessages id="incidentPage.settings.ruleUsers" />}
                          >
                            {renderUserSelect('incidentPage.settings.notificationUserLabel')}
                          </Form.Item>
                        </Col>
                      </Row>

                      {fields.length > 1 && (
                        <Row>
                          <Col xs={24} style={{ textAlign: 'right' }}>
                            <Button
                              type="text"
                              icon={<DeleteOutlined />}
                              danger
                              onClick={() => remove(field.name)}
                            >
                              <IntlMessages id="incidentPage.settings.removeNotificationRule" />
                            </Button>
                          </Col>
                        </Row>
                      )}
                    </RuleCard>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ ...EMPTY_RULE })}
                    block
                  >
                    <IntlMessages id="incidentPage.settings.addNotificationRule" />
                  </Button>
                  <Text type="secondary">
                    <IntlMessages id="incidentPage.settings.notificationHelper" />
                  </Text>
                </Space>
              )}
            </Form.List>
          </Space>
        </Form>
      </Spin>
    </Modal>
  );
};

export default IncidentSettingsModal;
